import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Crown, Globe2, Route, Award, Instagram, Radio, PlayCircle, MessagesSquare, Sprout,
  LogOut, Diamond, ArrowRight, X, CalendarDays, Clock, Sparkles, MessageCircle,
  ShieldCheck, MailCheck, Star, Quote, BellRing, CheckCircle2, Users,
} from "lucide-react";
import { guestApi, getUser, logout } from "../lib/api";

const WHATSAPP_ASESOR = "https://wa.me/message/XD5YLDWYNUHRN1";

const IMG = {
  leadership: "https://images.unsplash.com/flagged/photo-1568777567165-aaaa23be84a1?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
  world: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
  method: "https://images.pexels.com/photos/8535166/pexels-photo-8535166.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  certifications: "https://images.unsplash.com/photo-1519070994522-88c6b756330e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
  experiences: "https://images.unsplash.com/photo-1544476866-ce192b63bd7f?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
  webinar: "https://images.unsplash.com/photo-1640222491592-943238631a21?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
  past: "https://images.unsplash.com/photo-1641683521844-700c456379bd?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000",
  recruitment: "https://images.pexels.com/photos/8927026/pexels-photo-8927026.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  hero: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
};

const TILES = [
  { key: "leadership", icon: Crown, kind: "modal", label: "Ser Supera Leadership", sub: "Más que un curso: una identidad." },
  { key: "world", icon: Globe2, kind: "modal", label: "Supera en el Mundo", sub: "Una comunidad sin fronteras." },
  { key: "method", icon: Route, kind: "modal", label: "Programas y Metodología", sub: "Un camino para cada edad." },
  { key: "certifications", icon: Award, kind: "modal", label: "Certificaciones Internacionales", sub: "Liderazgo que el mundo reconoce." },
  { key: "experiences", icon: Instagram, kind: "instagram", label: "Experiencias Supera", sub: "Vive Supera en Instagram." },
  { key: "webinar", icon: Radio, kind: "upcoming", label: "Webinar Supera en Vivo", sub: "Próximas sesiones especiales." },
  { key: "past", icon: PlayCircle, kind: "past", label: "Sesiones Pasadas", sub: "Elige la que más te inspire." },
  { key: "chat", icon: MessagesSquare, kind: "chat", label: "Chat Supera", sub: "Agente virtual o staff Supera." },
  { key: "recruitment", icon: Sprout, kind: "recruitment", label: "Iniciar Reclutamiento", sub: "Únete al equipo Supera." },
];

export default function GuestPortal() {
  const navigate = useNavigate();
  const user = getUser();
  const [content, setContent] = useState(null);
  const [modalKey, setModalKey] = useState(null);
  const [sheet, setSheet] = useState(null); // 'upcoming' | 'past'
  const [webinars, setWebinars] = useState([]);
  const [loadingW, setLoadingW] = useState(false);
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [status, setStatus] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);

  useEffect(() => { guestApi.content().then(setContent).catch(() => {}); }, []);
  useEffect(() => { guestApi.status().then(setStatus).catch(() => {}); }, []);

  const sendCode = async () => {
    try { const r = await guestApi.sendEmailCode(); setCodeSent(true);
      toast.success(r.email_configured ? "Te enviamos un código a tu correo." : "Sistema de correo en configuración. Intenta más tarde.");
    } catch { toast.error("No se pudo enviar el código."); }
  };
  const confirmCode = async () => {
    try { await guestApi.confirmEmailCode(code.trim()); toast.success("¡Correo verificado! 🎉");
      setVerifyOpen(false); setStatus((s) => ({ ...s, email_verified: true }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Código incorrecto."); }
  };

  const phrases = content?.marketing_phrases || ["Formamos líderes, no seguidores."];
  useEffect(() => {
    const t = setInterval(() => setPhraseIdx((i) => (i + 1) % phrases.length), 4000);
    return () => clearInterval(t);
  }, [phrases.length]);

  const openSheet = async (tipo) => {
    setSheet(tipo); setLoadingW(true);
    if (tipo === "upcoming") guestApi.event("webinar_open");
    try { setWebinars(await guestApi.webinars(tipo)); } catch { setWebinars([]); }
    setLoadingW(false);
  };

  const handleTile = (t) => {
    if (t.kind === "modal") { guestApi.event("tile_open"); setModalKey(t.key); }
    else if (t.kind === "instagram") { guestApi.event("instagram_open"); window.open(content?.instagram_url || "https://www.instagram.com/liderazgosupera", "_blank"); }
    else if (t.kind === "upcoming") openSheet("upcoming");
    else if (t.kind === "past") openSheet("past");
    else if (t.kind === "chat") navigate("/chat");
    else if (t.kind === "recruitment") navigate("/registro/reclutamiento");
  };

  const registerWebinar = async (w) => {
    try {
      const r = await guestApi.registerWebinar(w.id);
      toast.success(r.already ? "Ya estabas inscrito a esta sesión." : "¡Listo! Quedaste inscrito y te enviaremos un recordatorio. 🔔");
      setWebinars(await guestApi.webinars("upcoming"));
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo completar la inscripción."); }
  };

  const signOut = () => { logout(); navigate("/"); };
  const tile = modalKey ? (content?.tiles?.[modalKey] || {}) : null;

  return (
    <div className="min-h-screen bg-[#F6F4EE]" data-testid="guest-portal">
      {/* Hero */}
      <div className="relative bg-[#0A1C30] overflow-hidden">
        <img src={IMG.hero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1C30]/70 via-[#0A1C30]/85 to-[#0A1C30]" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
          <div className="h-16 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Diamond className="w-5 h-5 text-[var(--gold)] fill-[var(--gold)]" />
              <span className="font-extrabold tracking-[0.35em] text-white text-sm">SUPERA</span>
            </div>
            <button onClick={signOut} data-testid="guest-logout" title="Salir" className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold transition-colors">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
          <div className="pt-8 pb-16 sm:pb-20 max-w-2xl">
            <div key={phraseIdx} className="inline-flex items-center gap-2 rounded-full bg-[var(--gold)]/15 ring-1 ring-[var(--gold)]/30 px-3.5 py-1.5 mb-5 animate-in fade-in slide-in-from-bottom-2">
              <Sparkles className="w-3.5 h-3.5 text-[var(--gold)]" />
              <span className="text-[var(--gold)] text-xs font-bold tracking-wide">{phrases[phraseIdx]}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-white leading-[1.1] [text-wrap:balance]">
              {content?.portal?.title || "Bienvenido a Supera"}
            </h1>
            <p className="mt-4 text-base text-white/70 leading-relaxed">
              {content?.portal?.subtitle || "Descubre el camino para formar líderes de verdad."}
            </p>
            <p className="mt-3 text-sm text-white/50">Hola, {user?.nombre || "futuro líder"} 👋</p>
          </div>
        </div>
      </div>

      {/* Verificación de correo */}
      {status && !status.email_verified && (
        <div className="max-w-6xl mx-auto px-5 sm:px-8 mt-6" data-testid="verify-banner">
          <div className="flex flex-col sm:flex-row items-center gap-3 rounded-2xl bg-[#FFF6E0] ring-1 ring-[var(--gold)]/40 px-5 py-3.5">
            <MailCheck className="w-5 h-5 text-[#B99B2E] shrink-0" />
            <p className="text-sm text-[#7a6410] font-semibold flex-1 text-center sm:text-left">Verifica tu correo para recibir tu material y recordatorios de las sesiones en vivo.</p>
            <button onClick={() => { setVerifyOpen(true); }} data-testid="verify-open-btn" className="rounded-full bg-[var(--gold)] hover:bg-[#d8b53f] text-[#0A1C30] font-extrabold px-5 py-2 text-sm transition-colors">Verificar ahora</button>
          </div>
        </div>
      )}

      {/* Tiles */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 -mt-8 pb-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {TILES.map((t) => {
            const Icon = t.icon;
            const title = t.kind === "modal" ? (content?.tiles?.[t.key]?.title || t.label) : t.label;
            const sub = t.kind === "modal" ? (content?.tiles?.[t.key]?.subtitle || t.sub) : t.sub;
            return (
              <button
                key={t.key}
                data-testid={`guest-tile-${t.key}`}
                onClick={() => handleTile(t)}
                className="group relative text-left rounded-2xl overflow-hidden ring-1 ring-black/5 shadow-sm hover:shadow-xl transition-all duration-300 h-44 sm:h-52 bg-[#0A1C30]"
              >
                <img src={IMG[t.key]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1C30] via-[#0A1C30]/50 to-transparent" />
                <div className="relative h-full flex flex-col justify-between p-4 sm:p-5">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white/10 backdrop-blur ring-1 ring-white/20">
                    <Icon className="w-5 h-5 text-[var(--gold)]" />
                  </span>
                  <div>
                    <p className="text-white font-extrabold text-base sm:text-lg leading-tight">{title}</p>
                    <p className="text-white/60 text-xs mt-1 flex items-center gap-1">{sub} <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" /></p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Prueba social: stats + testimonios */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4" data-testid="social-proof">
        <div className="grid grid-cols-3 gap-3 sm:gap-5 mb-8">
          {(content?.stats || [{ value: "+20,000", label: "líderes formados" }, { value: "+23", label: "países" }, { value: "+10", label: "años" }]).map((s, i) => (
            <div key={i} className="text-center rounded-2xl bg-white ring-1 ring-black/5 py-5">
              <p className="text-2xl sm:text-4xl font-black text-[#0A1C30]">{s.value}</p>
              <p className="text-xs sm:text-sm text-[#6B7688] font-semibold mt-1">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mb-4">
          <Star className="w-4 h-4 text-[var(--gold)] fill-[var(--gold)]" />
          <h2 className="font-black text-lg text-[#0A1C30]">Familias que ya viven Supera</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {(content?.testimonials || []).map((tm, i) => (
            <div key={i} data-testid={`testimonial-${i}`} className="rounded-2xl bg-white ring-1 ring-black/5 p-5">
              {tm.video_url ? (
                <a href={tm.video_url} target="_blank" rel="noreferrer" className="block mb-3 rounded-xl overflow-hidden relative h-32 bg-[#0A1C30]">
                  {tm.image && <img src={tm.image} alt="" className="w-full h-full object-cover opacity-70" />}
                  <span className="absolute inset-0 flex items-center justify-center"><PlayCircle className="w-10 h-10 text-white" /></span>
                </a>
              ) : tm.image ? (
                <img src={tm.image} alt="" className="w-full h-32 object-cover rounded-xl mb-3" />
              ) : (
                <Quote className="w-6 h-6 text-[var(--gold)] mb-2" />
              )}
              <p className="text-sm text-[#2A3446] leading-relaxed italic">“{tm.quote}”</p>
              <p className="mt-3 text-sm font-extrabold text-[#0A1C30]">{tm.name}</p>
              <p className="text-xs text-[#6B7688]">{tm.role}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Conversion CTA */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="rounded-3xl bg-[#0A1C30] p-6 sm:p-10 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, #C9A227 0, transparent 45%), radial-gradient(circle at 85% 80%, #C9A227 0, transparent 45%)" }} />
          <div className="relative">
            <h2 className="text-2xl sm:text-3xl font-black text-white [text-wrap:balance]">Tu hijo puede ser el líder que el mundo necesita.</h2>
            <p className="mt-3 text-white/60 text-sm max-w-xl mx-auto">Da el siguiente paso hoy. Un asesor Supera te acompañará para elegir el programa ideal según su edad y momento.</p>
            <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href={WHATSAPP_ASESOR} target="_blank" rel="noreferrer" data-testid="guest-cta-whatsapp" onClick={() => guestApi.event("asesor_click")}
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-extrabold px-7 py-3 text-sm transition-colors">
                <MessageCircle className="w-4 h-4" /> Hablar con un asesor
              </a>
              <button onClick={() => navigate("/registro/reclutamiento")} data-testid="guest-cta-enroll"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--gold)] hover:bg-[#d8b53f] text-[#0A1C30] font-extrabold px-7 py-3 text-sm transition-colors">
                Inscribir a mi hijo/a <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de secciones */}
      {modalKey && (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={() => setModalKey(null)} data-testid="guest-modal">
          <div className="bg-white rounded-3xl overflow-hidden max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-44">
              <img src={IMG[modalKey]} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1C30] to-transparent" />
              <button onClick={() => setModalKey(null)} data-testid="guest-modal-close" className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center"><X className="w-4 h-4 text-white" /></button>
              <div className="absolute bottom-4 left-5 right-5">
                <p className="text-[var(--gold)] text-xs font-bold">{tile?.subtitle}</p>
                <h3 className="text-white font-black text-2xl leading-tight">{tile?.title}</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-[#2A3446] text-sm leading-relaxed whitespace-pre-line">{tile?.body}</p>
              <a href={WHATSAPP_ASESOR} target="_blank" rel="noreferrer" className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--gold)] hover:bg-[#d8b53f] text-[#0A1C30] font-extrabold px-6 py-3 text-sm transition-colors">
                Quiero saber más <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Sheet de webinars */}
      {sheet && (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setSheet(null)} data-testid="guest-webinar-sheet">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-black/5 flex items-center justify-between">
              <h3 className="font-black text-lg text-[#0A1C30]">{sheet === "upcoming" ? "Próximas sesiones en vivo" : "Sesiones pasadas"}</h3>
              <button onClick={() => setSheet(null)} className="w-9 h-9 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center"><X className="w-4 h-4 text-[#0A1C30]" /></button>
            </div>
            <div className="p-5 space-y-3">
              {loadingW && <p className="text-center text-[#6B7688] py-8 text-sm">Cargando…</p>}
              {!loadingW && webinars.length === 0 && (
                <p className="text-center text-[#6B7688] py-10 text-sm">{sheet === "upcoming" ? "Aún no hay sesiones programadas. ¡Vuelve pronto!" : "Todavía no hay grabaciones disponibles."}</p>
              )}
              {!loadingW && webinars.map((w) => (
                <div key={w.id} data-testid={`webinar-${w.id}`} className="flex gap-4 rounded-2xl ring-1 ring-black/5 overflow-hidden bg-[#FAF8F1]">
                  <img src={w.image || IMG[sheet === "upcoming" ? "webinar" : "past"]} alt="" className="w-24 sm:w-28 object-cover" />
                  <div className="flex-1 py-3 pr-3">
                    <p className="font-extrabold text-[#0A1C30] text-sm leading-tight">{w.titulo}</p>
                    {w.descripcion && <p className="text-xs text-[#6B7688] mt-1 line-clamp-2">{w.descripcion}</p>}
                    <div className="flex items-center gap-3 mt-2 text-xs text-[#6B7688] font-semibold">
                      {w.fecha && <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {w.fecha}</span>}
                      {w.hora && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {w.hora}</span>}
                    </div>
                    {sheet === "upcoming" && w.cupos > 0 && (
                      <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-[#E0533D]">
                        <BellRing className="w-3 h-3" /> ¡Cupos limitados! {Math.max(0, w.cupos - (w.inscritos || 0))} lugares disponibles
                      </p>
                    )}
                    {sheet === "upcoming" ? (
                      w.mine ? (
                        <span className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-[#E8F3EC] text-[#22A860] font-extrabold px-4 py-1.5 text-xs">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Inscrito · te recordaremos
                        </span>
                      ) : (
                        <button onClick={() => registerWebinar(w)} data-testid={`webinar-register-${w.id}`}
                          className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-[var(--gold)] hover:bg-[#d8b53f] text-[#0A1C30] font-extrabold px-4 py-1.5 text-xs transition-colors">
                          <BellRing className="w-3.5 h-3.5" /> Regístrate y recuérdamelo
                        </button>
                      )
                    ) : w.recording_url ? (
                      <a href={w.recording_url} target="_blank" rel="noreferrer" className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-[#0A1C30] hover:bg-[#12294a] text-white font-bold px-4 py-1.5 text-xs transition-colors">
                        <PlayCircle className="w-3.5 h-3.5" /> Ver sesión
                      </a>
                    ) : (
                      <span className="mt-2.5 inline-block text-xs text-[#9AA4B4]">Grabación próximamente</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Modal verificación de correo */}
      {verifyOpen && (
        <div className="fixed inset-0 z-[85] bg-black/60 flex items-center justify-center p-4" onClick={() => setVerifyOpen(false)} data-testid="verify-modal">
          <div className="bg-white rounded-3xl w-full max-w-sm p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 rounded-2xl bg-[#FFF6E0] flex items-center justify-center mx-auto mb-3"><ShieldCheck className="w-7 h-7 text-[#B99B2E]" /></div>
            <h3 className="font-black text-lg text-[#0A1C30]">Verifica tu correo</h3>
            <p className="text-sm text-[#6B7688] mt-1 mb-4">{codeSent ? "Escribe el código de 6 dígitos que enviamos a tu correo." : "Te enviaremos un código a tu correo para confirmar tu registro."}</p>
            {!codeSent ? (
              <button onClick={sendCode} data-testid="verify-send-code" className="w-full rounded-xl bg-[var(--gold)] hover:bg-[#d8b53f] text-[#0A1C30] font-extrabold py-3 text-sm">Enviarme el código</button>
            ) : (
              <>
                <input value={code} onChange={(e) => setCode(e.target.value)} data-testid="verify-code-input" maxLength={6} placeholder="______"
                  className="w-full text-center tracking-[0.5em] text-2xl font-black rounded-xl border border-black/10 py-3 mb-3 focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40" />
                <button onClick={confirmCode} data-testid="verify-confirm-code" className="w-full rounded-xl bg-[var(--gold)] hover:bg-[#d8b53f] text-[#0A1C30] font-extrabold py-3 text-sm">Confirmar</button>
                <button onClick={sendCode} className="mt-2 text-xs text-[#6B7688] font-semibold hover:underline">Reenviar código</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
